import { GameLoader } from "../loaders/GameLoader";
import type { PromoAction } from "../types/IGameLoader.types";

interface IPromoCommunicationService {
    isPromoToolInited: boolean;
    requestGameReload(): void;
}

export class PromoCommunicationService implements IPromoCommunicationService{
    private award: any = null;
    private _isPromoToolInited: boolean = false;

    private lastSentToPromoOnGameIdleOrActive: string | null = null;
    private isGameActive: boolean = true;
    private isContentVisibleInShellFeature: boolean = false;

    set isPromoToolInited(value: boolean) {
        this._isPromoToolInited = value;
        this.gameLoader.setActivePromoWidget(value);
    }

    get isPromoToolInited() {
        return this._isPromoToolInited
    }

    constructor(
        private gameLoader: GameLoader,
        private iframe: HTMLIFrameElement,
        private isDebug: boolean
    ) {
        this.subscribe();
    }

    public requestGameReload(): void {
        this.postToPromo("gameReload");
    }

    private subscribe(): void {

        // Listener from Shell Feature
        window.addEventListener("shell-feature-content-visible", (event: CustomEvent<boolean>) => {
            this.isContentVisibleInShellFeature = event.detail;
            this.promoOnGameIdleOrActive();
            this.promoShowBonusPopupIfExist();
            this.isContentVisibleInShellFeature ? this.promoHide() : this.promoShow();
            this.consoleLog('From Shell Feature:', 'content visible', this.isContentVisibleInShellFeature);
        });

        window.addEventListener("message", (message) => {
            if (!this.iframe || !message.data) {
                return;
            }

            try {
                const { name, data, action } = message.data || {};

                // Promo Tool events
                if (action) {
                    this.consoleLog('From Promo', action, data);

                    switch (action) {
                        case "bonusPopupResponse":
                            this.gameBonusPopupResponse(message);
                            break;
                        case "promoInited":
                            this.isPromoToolInited = true;
                            this.promoShow();
                            this.gamePromoInited();
                            this.promoShowBonusPopupIfExist();
                            break;
                    }
                }

                // Game events
                if (name) {
                    if (!this.iframe.contentWindow) {
                        return;
                    }

                    this.consoleLog('From Game', name, data);

                    switch (name) {
                        case "widget:award_available":
                            this.award = data;
                            this.promoShowBonusPopupIfExist();
                            break;
                        case "widget:award_init":
                            this.promoInit(data);
                            break;
                        case "widget:award_bet_changed":
                            this.promoBetChanged(data);
                            break;
                        case "widget:award_on_game_idle":
                            this.isGameActive = false;
                            this.promoOnGameIdleOrActive();
                            this.promoShowBonusPopupIfExist();
                            break;
                        case "widget:award_on_game_active":
                            this.isGameActive = true;
                            this.promoOnGameIdleOrActive();
                            break;
                        case "widget:game_killed":
                            this.promoGameKilled();
                            break;
                        case "widget:on_spin_finished":
                            this.promoSpinFinished();
                            break;
                        case "[shell-feature]":
                            message.data.visible ? this.promoShow() : this.promoHide();
                            break;
                    }
                }
            } catch (ep: any) {
                console.warn(`PostMessage transfer unavailable: ${ep.message}`);
            }
        });

        window.addEventListener("beforeunload", this.promoBeforeunload.bind(this));
    }

    // Promo Tool Actions

    private promoBeforeunload() {
        window.removeEventListener("beforeunload", this.promoBeforeunload);
        this.postToPromo("hide");
    }

    private promoInit(data: any) {
        this.postToPromo("init", {
            bet: data.bet, // current user bet in cents
            currencyFormat: data.currencyFormat,
            widgetParams: {
                landscape: {
                    visible: true, //is widget visible always
                    initialVisible: false, // is widget visible on start
                },
                portrait: {
                    visible: true, //is widget visible always
                    initialVisible: false, // is widget visible on start
                },
            }
        });
    }

    private promoShow() {
        this.postToPromo("show");
    }

    private promoHide() {
        this.postToPromo("hide");
    }

    private promoBetChanged(data: any) {
        this.postToPromo("onBetChanged", data.bet);
    }

    private promoShowBonusPopupIfExist() {
        if (!this.award || this.isGameActive || this.isContentVisibleInShellFeature || !this.isPromoToolInited) return;

        this.award.source = this.award?.source || this.award?.event || "operator";
        this.postToPromo("showBonusPopup", {
            type: "award",
            data: this.award,
        });
    }

    private promoOnGameIdleOrActive() {
        const currentState = (!this.isContentVisibleInShellFeature && !this.isGameActive)
            ? "onGameIdle"
            : "onGameActive";

        if (this.lastSentToPromoOnGameIdleOrActive !== currentState) {
            this.postToPromo(currentState);
            this.lastSentToPromoOnGameIdleOrActive = currentState;
        }
    }

    private promoGameKilled() {
        this.postToPromo("gameKilled");
    }

    private promoSpinFinished() {
        this.postToPromo("onSpinFinished");
    }

    private postToPromo = (action: PromoAction, payload: any = {}) => {
        const message = { from: "GAME", to: "PROMO", action, payload };
        this.consoleLog("Message to Promo", message, payload);
        window.parent.postMessage(message, "*");
    };

    // Game Actions

    private gameBonusPopupResponse(data: any) {
        this.postGame("widget:award_claimed", this.award);
        this.award = null;
    }

    private gamePromoInited() {
        this.postGame("widget:award_promo_inited");
    }

    private postGame = (name: string, data: any = '') => {
        if (!this.iframe || !this.iframe.contentWindow) {
            return;
        }

        const message = { name, data };
        this.consoleLog("Message to Game", message, data);
        this.iframe.contentWindow.postMessage(message, "*");
    }

    // *****
    private consoleLog = (place: string, message: any, data: any = '') => {
        if (this.isDebug) {
            console.log(place, message,data );
        }
    }
}
